import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCycles, useCompensations, createCycle, updateCycle, deleteCycle } from '../../hooks/useFirestore';
import { CYCLE_STATUSES } from '../../constants';
import { formatDate } from '../../utils/formatters';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Notice from '../../components/ui/Notice';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatCard from '../../components/ui/StatCard';
import AnimatedList from '../../components/ui/AnimatedList';
import { SkeletonGrid } from '../../components/ui/Skeleton';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../utils/cn';

export default function AdminDashboard() {
  const { cycles, loading } = useCycles();
  const nav = useNavigate();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newEffectiveDate, setNewEffectiveDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createCycle({ name: newName.trim(), description: newDesc.trim(), effectiveDate: newEffectiveDate || '' });
      toast.success('Cycle created');
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setNewEffectiveDate('');
    } catch (_e) {
      toast.error('Failed to create cycle');
    }
    setCreating(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCycle(deleteTarget.id);
      toast.success('Cycle deleted');
    } catch {
      toast.error('Failed to delete cycle');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleStatusChange = async (cycle, newStatus) => {
    try {
      await updateCycle(cycle.id, { status: newStatus });
      toast.success(`Cycle set to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  // Stats
  const activeCycles = cycles.filter((c) => c.status === 'active');
  const countByStatus = (status) => cycles.filter((c) => (c.status || 'draft') === status).length;
  const totalStatements = cycles.reduce((sum, c) => sum + (c.compCount || 0), 0);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-end justify-between gap-4"
      >
        <div>
          <h1 className="text-display-sm text-navy-900">Compensation Cycles</h1>
          <p className="text-navy-400 mt-1">Create and manage compensation review cycles.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} size="lg">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Cycle
        </Button>
      </motion.div>

      {/* Stats: the current cycle and all cycles are kept separate */}
      {!loading && cycles.length > 0 && (
        <div className="space-y-6">
          {activeCycles.length > 1 && (
            <Notice tone="warning" title={`${activeCycles.length} cycles are active at the same time`}>
              Communicators see every active cycle. Complete the old cycle before starting comms for the new one.
            </Notice>
          )}
          <section className="space-y-3">
            <h2 className="text-xs font-semibold text-navy-400 uppercase tracking-wider">Current cycle</h2>
            {activeCycles.length === 0 ? (
              <p className="text-sm text-navy-400">No cycle is active. Activate a draft cycle to start comms.</p>
            ) : (
              activeCycles.map((cycle) => <ActiveCycleStats key={cycle.id} cycle={cycle} />)
            )}
          </section>
          <section className="space-y-3">
            <h2 className="text-xs font-semibold text-navy-400 uppercase tracking-wider">All cycles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                label="Cycles"
                value={cycles.length}
                sub={`${countByStatus('active')} active · ${countByStatus('draft')} draft · ${countByStatus('completed')} completed`}
                icon="📋"
                index={0}
              />
              <StatCard
                label="Compensation statements"
                value={totalStatements}
                sub="Across all cycles; someone in two cycles counts twice"
                icon="💰"
                index={1}
              />
            </div>
          </section>
        </div>
      )}

      {/* Cycle cards */}
      {loading ? (
        <SkeletonGrid count={3} />
      ) : cycles.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-24 text-center"
        >
          <div className="w-20 h-20 rounded-3xl bg-surface-100 flex items-center justify-center text-4xl mb-6">📊</div>
          <h3 className="text-xl font-bold text-navy-900 mb-2">No cycles yet</h3>
          <p className="text-navy-400 mb-6 max-w-sm">Create your first compensation cycle to get started.</p>
          <Button onClick={() => setShowCreate(true)}>Create First Cycle</Button>
        </motion.div>
      ) : (
        <AnimatedList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cycles.map((cycle) => {
            const st = CYCLE_STATUSES[cycle.status] || CYCLE_STATUSES.draft;
            return (
              <Card key={cycle.id} hover onClick={() => nav(`/admin/cycles/${cycle.id}`)} className="p-6 group">
                {/* Status + Actions */}
                <div className="flex items-center justify-between mb-4">
                  <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset', st.color, st.ring)}>
                    {st.label}
                  </span>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {cycle.status === 'draft' && (
                      <button onClick={() => handleStatusChange(cycle, 'active')} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Activate">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </button>
                    )}
                    {cycle.status === 'active' && (
                      <>
                        <button onClick={() => handleStatusChange(cycle, 'draft')} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500" title="Revert to Draft">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                        </button>
                        <button onClick={() => handleStatusChange(cycle, 'completed')} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Complete">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </button>
                      </>
                    )}
                    {cycle.status === 'completed' && (
                      <button onClick={() => handleStatusChange(cycle, 'active')} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Reopen as Active">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget(cycle)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                    </button>
                  </div>
                </div>
                {/* Name + Description */}
                <h3 className="text-lg font-bold text-navy-900 mb-1 group-hover:text-accent-700 transition-colors">{cycle.name}</h3>
                {cycle.description && <p className="text-sm text-navy-400 mb-4 line-clamp-2">{cycle.description}</p>}
                {/* Footer stats */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-surface-200/80 text-xs text-navy-400">
                  <span className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
                    {cycle.compCount || 0} employees
                  </span>
                  <span>{formatDate(cycle.effectiveDate || cycle.createdAt)}</span>
                </div>
              </Card>
            );
          })}
        </AnimatedList>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Compensation Cycle" size="sm">
        <div className="space-y-4">
          <Input label="Cycle Name" placeholder="e.g., 2026 Annual Review" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
          <Input label="Description (optional)" placeholder="Brief description…" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
          <Input label="Effective Date" type="date" value={newEffectiveDate} onChange={(e) => setNewEffectiveDate(e.target.value)} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={creating} disabled={!newName.trim()}>Create Cycle</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Cycle"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? All compensations and the reporting structure in this cycle will be permanently removed.`}
        confirmLabel="Delete"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function ActiveCycleStats({ cycle }) {
  const { comps, loading } = useCompensations(cycle.id);
  const nav = useNavigate();
  const done = comps.filter((c) => c._presented).length;
  const total = comps.length;
  const show = (value) => (loading ? '…' : value);
  return (
    <div className="space-y-2">
      <button
        onClick={() => nav(`/admin/cycles/${cycle.id}`)}
        className="text-sm font-semibold text-navy-900 hover:text-accent-700 transition-colors"
      >
        {cycle.name} →
      </button>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Employees" value={show(total)} icon="👥" index={0} />
        <StatCard label="Communicated" value={show(`${done}/${total} (${total ? Math.round((done / total) * 100) : 0}%)`)} icon="✅" index={1} />
        <StatCard label="Pending" value={show(`${total - done}/${total}`)} icon="⏳" index={2} />
      </div>
    </div>
  );
}
